import { getGetCampaignApiV1QueryKey, useSubmitCampaignVideoApiV1 } from "@/api/__generated__/api.schema";
import { routes } from "@/constants/url";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useWorkspaceNavigate } from "@/lib/router/use-workspace-navigate";
import { useCampaignId } from "@/routes/campaigns/hooks/use-campaign-id";
import { Button, Input, Typography, cn } from "@backpac/monica";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export function FormCampaignJoinPage() {
  const campaignId = useCampaignId();
  const navigate = useWorkspaceNavigate();
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return (
      <div className="h-screen px-4 desktop:px-0">
        <i className="i-bp-check inline-block size-8 mb-4" />
        <Header
          className="mb-8"
          title="Your video has been submitted"
          description={`Thanks for submitting!\nWe'll review your video soon and notify you once your reward is confirmed.`}
        />
        <Button className="w-full" size="lg" onClick={() => navigate(routes.CAMPAIGNS._view(campaignId))}>
          Back to Home
        </Button>
      </div>
    );
  }

  return (
    <div className="h-screen px-4 desktop:px-0">
      <Header
        className="mb-3"
        title="Submit your video URL"
        description="Share your video link here so we can review it and confirm your reward. Please make sure the link is public and the video matches the campaign guidelines."
      />
      <VideoUrlForm onSubmit={() => setSubmitted(true)} />
    </div>
  );
}

const VideoUrlFormSchema = z.object({
  videoUrl: z.string().refine(validateTiktokUrl, {
    message: "Invalid video URL",
  }),
});

function VideoUrlForm({ onSubmit }: { onSubmit: () => void }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<z.infer<typeof VideoUrlFormSchema>>({
    resolver: zodResolver(VideoUrlFormSchema),
  });
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const campaignId = useCampaignId();
  const { mutate: submitCampaignVideo, isPending } = useSubmitCampaignVideoApiV1({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetCampaignApiV1QueryKey(workspaceId, campaignId) });
      },
    },
  });

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={handleSubmit((data) => {
        submitCampaignVideo(
          {
            workspaceId: workspaceId,
            campaignId: campaignId,
            data: {
              videoUrl: data.videoUrl,
            },
          },
          {
            onSuccess: () => {
              onSubmit();
            },
          },
        );
      })}
    >
      <div className="flex-1 gap-2">
        <label htmlFor="videoUrl" className="text-xs">
          Video URL
        </label>
        <Input
          className="w-full bg-white"
          placeholder="Video URL"
          {...register("videoUrl", {
            required: "Video URL is required",
          })}
        />
        {errors.videoUrl && <p className="text-red-500 text-sm mt-1">{errors.videoUrl.message}</p>}
      </div>
      <Button
        className="flex-shrink-0 mt-auto w-full"
        size="lg"
        type="submit"
        loading={isSubmitting || isPending}
        disabled={!isValid || !isDirty}
      >
        Submit video
      </Button>
    </form>
  );
}

function Header({ title, description, className }: { title: string; description: string; className?: string }) {
  return (
    <div className={cn("space-y-3", className)}>
      <Typography asChild font="unbounded" size="xl">
        <h1>{title}</h1>
      </Typography>
      <Typography size="paragraphXs" color="textMuted" className="whitespace-pre-wrap">
        {description}
      </Typography>
    </div>
  );
}

function validateTiktokUrl(url: string) {
  return url.includes("tiktok.com");
}
